import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from "recharts";
import { Check, RotateCcw, FlaskConical, Info } from "lucide-react";
import api, { apiError } from "../lib/api";
import { Money, usePrivacy } from "../context/PrivacyContext";
import { fmtEUR, fmtDate } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Slider } from "../components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

const GROUP_COLORS = { "Dinero disponible": "#0A0A0A", "Inversiones": "#3B82F6", "Inmuebles": "#8B5CF6", "Vehículos": "#EC4899", "Criptomonedas": "#F0B90B", "Otros bienes": "#8F8F94" };

function BalanceRow({ asset, onSaved }) {
  const [value, setValue] = useState(String(asset.balance));
  const dirty = parseFloat(String(value).replace(",", ".")) !== asset.balance;

  const save = async () => {
    try {
      await api.put(`/assets/${asset.id}`, { name: asset.name, type: asset.type, balance: parseFloat(String(value).replace(",", ".")) || 0 });
      toast.success(`Saldo de ${asset.name} actualizado`);
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-border last:border-0">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{asset.name}</div>
        <div className="text-xs text-muted-foreground">{asset.type}</div>
      </div>
      <Input
        data-testid={`balance-input-${asset.id}`}
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && dirty && save()}
        className="w-32 h-9 text-right tabular"
      />
      <button
        data-testid={`balance-save-${asset.id}`}
        onClick={save}
        disabled={!dirty}
        className={`h-9 w-9 flex items-center justify-center rounded-lg border transition-colors ${dirty ? "border-foreground bg-primary text-primary-foreground" : "border-border text-muted-foreground opacity-40"}`}
      >
        <Check size={15} />
      </button>
    </div>
  );
}

export default function Patrimonio() {
  const [data, setData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [sim, setSim] = useState({});
  const { hidden } = usePrivacy();

  const load = () => {
    api.get("/networth").then((r) => setData(r.data));
    api.get("/assets").then((r) => setAssets(r.data));
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    for (const a of assets) {
      const key = { Banco: "Dinero disponible", Tarjeta: "Dinero disponible", Revolut: "Dinero disponible", Wise: "Dinero disponible", Efectivo: "Dinero disponible", Criptomonedas: "Dinero disponible", Binance: "Dinero disponible", Coinbase: "Dinero disponible", "Fondos monetarios": "Dinero disponible", eToro: "Inversiones", "Fondos indexados": "Inversiones", Acciones: "Inversiones", "Planes de pensiones": "Inversiones", Viviendas: "Inmuebles", Locales: "Inmuebles", Garajes: "Inmuebles", Terrenos: "Inmuebles", Inmuebles: "Inmuebles", "Vehículos": "Vehículos", Motos: "Vehículos", Autocaravanas: "Vehículos", Maquinaria: "Vehículos" }[a.type] || "Otros bienes";
      (g[key] = g[key] || []).push(a);
    }
    return g;
  }, [assets]);

  const simResult = useMemo(() => {
    if (!data) return null;
    let simBruto = 0;
    const rows = data.by_group.map((g) => {
      const pct = sim[g.grupo] ?? 0;
      const simulated = g.total * (1 + pct / 100);
      simBruto += simulated;
      return { ...g, pct_change: pct, simulated: Math.round(simulated * 100) / 100 };
    });
    return { rows, bruto: Math.round(simBruto * 100) / 100, neto: Math.round((simBruto - data.deuda) * 100) / 100, delta: Math.round((simBruto - data.bruto) * 100) / 100 };
  }, [data, sim]);

  if (!data) return <div className="text-sm text-muted-foreground">Cargando patrimonio…</div>;

  const hasSim = Object.values(sim).some((v) => v !== 0);

  return (
    <div className="space-y-6" data-testid="patrimonio-page">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tighter">Patrimonio total</h1>
        <p className="text-sm text-muted-foreground mt-1">Binance, Coinbase, eToro, bancos… todo tu patrimonio y su evolución.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
        {[
          ["Patrimonio bruto", data.bruto, "networth-bruto", "", null],
          ["Deuda actual", data.deuda_actual ?? data.deuda, "networth-deuda-actual", "text-red-600", null],
          ["Patrimonio neto real", data.neto_real ?? data.neto, "networth-neto-real", "text-emerald-600", null],
          ["Compromisos futuros", data.compromisos_futuros ?? data.deuda, "networth-compromisos", "text-amber-600", null],
          ["Posición neta vinculada", data.posicion_neta_vinculada ?? 0, "networth-posicion-vinculada",
            (data.posicion_neta_vinculada ?? 0) >= 0 ? "text-emerald-600" : "text-red-600",
            `Suma de (valor activo − liquidación) para las ${data.posicion_vinculada_count ?? 0} obligaciones con activo vinculado.`
          ],
        ].map(([l, v, id, cls, tip]) => (
          <div key={id} className="border border-border rounded-lg p-4 sm:p-5" data-testid={`${id}-card`}>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1">
              {l}
              {tip && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className="text-muted-foreground hover:text-foreground" aria-label={`Info ${l}`}>
                      <Info size={11} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" align="start" className="text-xs w-64 leading-relaxed">
                    {tip}
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <Money value={v} testId={id} className={`block mt-1 text-xl sm:text-2xl font-semibold tabular ${cls}`} />
          </div>
        ))}
      </div>
      <div className="rounded-md border border-border bg-secondary/30 p-3 text-xs leading-relaxed" data-testid="patrimonio-nota">
        <b>Patrimonio neto real</b> = Bruto − Deuda cancelable hoy (<i>liquidation_estimate</i>).<br />
        <b>Compromisos futuros</b> = Bruto − Coste total pendiente de pagar si mantienes todos los contratos hasta el final (<i>pending_amount</i>).<br />
        <b>Posición neta vinculada</b> = Σ (valor activo − liquidación) para obligaciones con activo asociado.<br />
        Ambas visiones son válidas y complementarias.
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Evolución */}
        <div className="border border-border rounded-lg p-5 lg:col-span-3">
          <h3 className="font-heading font-bold text-lg mb-4">Evolución del patrimonio neto</h3>
          {data.history.length < 2 ? (
            <p className="text-sm text-muted-foreground">Se registra un punto por día al actualizar tus saldos. Vuelve mañana para ver la evolución.</p>
          ) : null}
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <LineChart data={data.history} margin={{ top: 5, right: 5, bottom: 0, left: 5 }}>
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#8F8F94" }} tickLine={false} axisLine={false} tickFormatter={(d) => fmtDate(d).slice(0, 5)} />
                <YAxis tick={{ fontSize: 10, fill: "#8F8F94" }} tickLine={false} axisLine={false} tickFormatter={(v) => (hidden ? "•••" : `${Math.round(v / 1000)}k`)} width={40} />
                <Tooltip labelFormatter={fmtDate} formatter={(v) => (hidden ? "•••••• €" : fmtEUR(v))} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5EA", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="neto" name="Neto" stroke="#0A0A0A" strokeWidth={2.5} dot={data.history.length < 15} />
                <Line type="monotone" dataKey="bruto" name="Bruto" stroke="#8F8F94" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Distribución */}
        <div className="border border-border rounded-lg p-5 lg:col-span-2">
          <h3 className="font-heading font-bold text-lg mb-4">Distribución</h3>
          {data.by_group.length === 0 ? (
            <p className="text-sm text-muted-foreground">Añade cuentas y plataformas en Activos.</p>
          ) : (
            <>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart>
                    <Pie data={data.by_group} dataKey="total" nameKey="grupo" innerRadius={42} outerRadius={65} paddingAngle={2}>
                      {data.by_group.map((g) => <Cell key={g.grupo} fill={GROUP_COLORS[g.grupo] || "#8F8F94"} />)}
                    </Pie>
                    <Tooltip formatter={(v) => (hidden ? "•••••• €" : fmtEUR(v))} contentStyle={{ borderRadius: 8, border: "1px solid #E5E5EA", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-3" data-testid="networth-groups">
                {data.by_group.map((g) => (
                  <div key={g.grupo} className="flex items-center gap-2 text-sm">
                    <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: GROUP_COLORS[g.grupo] || "#8F8F94" }} />
                    <span className="text-muted-foreground">{g.grupo}</span>
                    <span className="text-xs text-muted-foreground">({g.pct}%)</span>
                    <Money value={g.total} className="font-medium tabular ml-auto" />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Actualizar saldos */}
      <div className="border border-border rounded-lg p-5">
        <h3 className="font-heading font-bold text-lg">Actualizar saldos</h3>
        <p className="text-xs text-muted-foreground mb-3">Registra manualmente el saldo actual de cada plataforma. Cada actualización guarda un punto en la evolución.</p>
        {assets.length === 0 ? (
          <p className="text-sm text-muted-foreground">No hay cuentas. Añádelas desde la sección Activos (Binance, Coinbase, eToro, Banco…).</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8" data-testid="balances-editor">
            {Object.entries(grouped).map(([grp, list]) => (
              <div key={grp}>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold mt-3 mb-1">{grp}</div>
                {list.map((a) => <BalanceRow key={a.id} asset={a} onSaved={load} />)}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Simulador */}
      <div className="border border-border rounded-lg p-5" data-testid="simulador-section">
        <div className="flex items-center gap-2">
          <FlaskConical size={18} />
          <h3 className="font-heading font-bold text-lg">Simulador de escenarios</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-4">¿Y si el mercado sube o baja? Ajusta cada grupo. No modifica tus datos reales.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-5">
            {data.by_group.map((g) => (
              <div key={g.grupo}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium">{g.grupo}</span>
                  <span className={`font-semibold tabular ${((sim[g.grupo] ?? 0) > 0) ? "text-emerald-600" : (sim[g.grupo] ?? 0) < 0 ? "text-red-600" : "text-muted-foreground"}`}>
                    {(sim[g.grupo] ?? 0) > 0 ? "+" : ""}{sim[g.grupo] ?? 0}%
                  </span>
                </div>
                <Slider
                  data-testid={`sim-slider-${g.grupo}`}
                  min={-50} max={50} step={1}
                  value={[sim[g.grupo] ?? 0]}
                  onValueChange={([v]) => setSim({ ...sim, [g.grupo]: v })}
                />
              </div>
            ))}
            {hasSim && (
              <button data-testid="sim-reset-button" onClick={() => setSim({})} className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">
                <RotateCcw size={13} /> Restablecer simulación
              </button>
            )}
          </div>
          <div className={`rounded-lg p-5 transition-colors ${hasSim ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
            <div className={`text-xs uppercase tracking-[0.18em] font-bold ${hasSim ? "text-white/60" : "text-muted-foreground"}`}>Patrimonio neto simulado</div>
            <Money value={simResult?.neto ?? data.neto} testId="sim-neto" className="block mt-1 text-3xl sm:text-4xl font-semibold tabular" />
            <div className="mt-3 text-sm">
              <span className={hasSim ? "text-white/60" : "text-muted-foreground"}>Variación: </span>
              <Money value={simResult?.delta ?? 0} testId="sim-delta" className={`font-semibold tabular ${(simResult?.delta ?? 0) >= 0 ? (hasSim ? "text-emerald-400" : "text-emerald-600") : (hasSim ? "text-red-400" : "text-red-600")}`} />
            </div>
            <div className={`mt-4 space-y-1 text-xs ${hasSim ? "text-white/70" : "text-muted-foreground"}`}>
              {simResult?.rows.filter((r) => r.pct_change !== 0).map((r) => (
                <div key={r.grupo} className="flex justify-between">
                  <span>{r.grupo} ({r.pct_change > 0 ? "+" : ""}{r.pct_change}%)</span>
                  <Money value={r.simulated} className="tabular" />
                </div>
              ))}
              {!hasSim && <span>Mueve los controles para simular un escenario.</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
